/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/7/22
 */

import React from "react";
import {colors, Grid, Typography, TypographyProps, useTheme} from "@mui/material";
import {formatValue, getAttributeLabel} from "../string-helper";

export type AttributeFormat = "string" | "number" | "boolean" | "date" | "datetime" | "gsacKey" | "helloKey" | undefined
export type AttributeConfig<T> = Attribute<T> | (string & keyof T);

export interface Attribute<T> {
	attribute?: string & keyof T
	label?: string;
	format?: AttributeFormat
	hidden?: boolean
	value?: any
}

export interface ModelViewProps<T extends Record<string, any>> {
	model: T
	nullValue?: React.ReactNode
	attrSx?: Record<string, any>
	labelSx?: Record<string, any>
	valueSx?: Record<string, any>
	labelTypographyProps?: TypographyProps
	valueTypographyProps?: TypographyProps
	nullTypographyProps?: TypographyProps
}

export interface ModelAttributesViewProps<T extends Record<string, any>> extends ModelViewProps<T> {
	attributes: AttributeConfig<T>[]
}

export default function ModelView<T extends Record<string, any>>(props: ModelAttributesViewProps<T>) {

	const theme = useTheme();
	const attributes = normalizeAttributes<T>(props.attributes);
	const nullValue = <Typography {...(props.nullTypographyProps||{})} sx={{ color: colors.grey[theme.palette.mode === "light" ? "300" : "700"] }}>(null)</Typography>;

	return (
		<div>
			{attributes.filter(a => !a.hidden).map(attribute =>
				<Attr
					key={attribute.label}
					sx={props.attrSx}
					labelSx={props.labelSx}
					valueSx={props.valueSx}
					label={attribute.label}
					labelTypographyProps={props.labelTypographyProps}
					value={getAttributeValue(props.model, attribute, nullValue, props.valueTypographyProps)}
				/>)}
		</div>
	);

}

function normalizeAttributes<T>(attributes: AttributeConfig<T>[]) {
	let attrs: Attribute<T>[] = [];
	attributes.forEach(attr => {
		let attribute, label, format, hidden = false;
		if (typeof attr === 'string' || attr instanceof String) {
			const parts = attr.split(":");
			attribute = parts[0] as string & keyof T;
			label = parts.length > 1 ? parts[1] : getAttributeLabel(attribute);
			format = (parts.length > 2 ? parts[2] : attribute) as AttributeFormat;
			attrs.push({attribute, label, format, hidden});
		} else {
			attribute = (attr as Attribute<T>).attribute;
			label = (attr as Attribute<T>).label || (attribute && getAttributeLabel(attribute));
			format = (attr as Attribute<T>).format || attribute as AttributeFormat;
			hidden = (attr as Attribute<T>).hidden ?? false;
			attrs.push({...(attr), ...{attribute, label, format, hidden}} as Attribute<T>);
		}
	});
	return attrs;
}

interface AttrProps {
	sx?: Record<string, any>
	labelSx?: Record<string, any>
	valueSx?: Record<string, any>
	labelTypographyProps?: TypographyProps
	label: string | undefined
	value: React.ReactNode
}

function Attr(props: AttrProps) {

	return (
		<Grid container sx={props.sx} wrap={"nowrap"}>
			<Grid item sx={props.labelSx}>
				<Typography {...(props.labelTypographyProps||{})}>{props.label}</Typography>
			</Grid>
			<Grid item sx={props.valueSx}>
				{props.value}
			</Grid>
		</Grid>
	);
}

function getAttributeValue<T>(model: T, attribute: Attribute<T>, nullValue: React.ReactNode, valueTypographyProps?: TypographyProps) {

	let value = null;

	if (attribute.value) {
		value = typeof attribute.value === "function" ? attribute.value(model) : attribute.value;
	} else if (attribute.attribute) {
		value = model[attribute.attribute]
	}

	if (value === null || value === undefined) {
		return nullValue;
	}

	return <Typography {...(valueTypographyProps||{})}>{attribute.value ? value : formatValue(attribute.format, value)}</Typography>

}